import { useMutation } from "@tanstack/react-query";
import { fetchClient } from "../generated/client";
import type { ShopDto, TextDto } from "../generated/schema";
import useInvalidateShop from "./use-invalidate-shop";

type UpdatePortalCustomTextParams = {
  key: string;
  texts: TextDto[];
};

/**
 * Upserts a single per-shop override for a widget translation key. Backed by PATCH
 * `/shops/:shopId/preferences/portals/custom-texts`.
 *
 * Uses `fetchClient` directly rather than `useApiMutation` because the path
 * is not yet reflected in the generated OpenAPI schema — it lands after
 * `pnpm codegen` runs against a backend with the `shop-preference-portal`
 * controller changes from this PR.
 */
export default function useUpdatePortalCustomText(shop: ShopDto) {
  const invalidateShop = useInvalidateShop(shop.id);

  return useMutation({
    mutationFn: async (variables: UpdatePortalCustomTextParams) => {
      const client = fetchClient as unknown as {
        PATCH: (
          url: string,
          init: {
            params: { path: { shopId: string } };
            body: UpdatePortalCustomTextParams;
          },
        ) => Promise<{ data?: ShopDto; error?: unknown }>;
      };
      const { data, error } = await client.PATCH(
        "/shops/{shopId}/preferences/portals/custom-texts",
        {
          params: { path: { shopId: shop.id } },
          body: variables,
        },
      );
      if (error) throw error;
      return data as ShopDto;
    },
    onSuccess: () => {
      invalidateShop();
    },
  });
}
